import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, TrendingUp, Users, QrCode, Calendar, 
  ArrowUpRight, ArrowDownRight, RefreshCw, Filter,
  Smartphone, FileText, Share2, HelpCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sourceIcons = {
  flyer: <FileText className="w-4 h-4" />,
  receipt: <QrCode className="w-4 h-4" />,
  display: <Smartphone className="w-4 h-4" />,
  social: <Share2 className="w-4 h-4" />,
  unknown: <HelpCircle className="w-4 h-4" />
};

const sourceColors = {
  flyer: 'bg-blue-500',
  receipt: 'bg-emerald-500',
  display: 'bg-purple-500',
  social: 'bg-pink-500',
  unknown: 'bg-gray-400'
};

export const QRAnalyticsDashboard = ({ businessId }) => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [businessId, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/qr/analytics/business`, {
        headers: { 'X-Business-Id': businessId }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching QR analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border/50">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      </div>
    );
  }

  if (!analytics || analytics.total_scans === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
        data-testid="qr-analytics-empty"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center text-white">
            <QrCode className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{t('qr_analytics.title') || 'Analíticas de Código QR'}</h3>
            <p className="text-sm text-muted-foreground">
              {t('qr_analytics.subtitle') || 'Mide el impacto de tus flyers y promociones'}
            </p>
          </div>
        </div>
        
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <QrCode className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-2">
            {t('qr_analytics.no_data') || 'Aún no hay escaneos registrados'}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('qr_analytics.tip') || 'Descarga tus flyers promocionales e imprime los códigos QR'}
          </p>
        </div>
      </motion.div>
    );
  }

  const conversionChange = analytics.conversion_rate > 5 ? 'positive' : 'neutral';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
      data-testid="qr-analytics-dashboard"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center text-white">
            <QrCode className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{t('qr_analytics.title') || 'Analíticas de Código QR'}</h3>
            <p className="text-sm text-muted-foreground">
              {t('qr_analytics.subtitle') || 'Mide el impacto de tus promociones'}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAnalytics}
          data-testid="refresh-qr-analytics"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4">
          <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Total Escaneos</p>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{analytics.total_scans}</p>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-xl p-4">
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-1">Conversiones</p>
          <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{analytics.total_conversions}</p>
        </div>
        <div className="bg-purple-50 dark:bg-purple-950/30 rounded-xl p-4">
          <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">Tasa Conversión</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{analytics.conversion_rate}%</p>
            {conversionChange === 'positive' && (
              <ArrowUpRight className="w-4 h-4 text-emerald-500" />
            )}
          </div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-4">
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">Mejor Fuente</p>
          <p className="text-lg font-bold text-amber-700 dark:text-amber-300 capitalize">
            {Object.entries(analytics.source_breakdown || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || '-'}
          </p>
        </div>
      </div>

      {/* Source Breakdown */}
      <div className="mb-6">
        <h4 className="text-sm font-medium mb-3">{t('qr_analytics.by_source') || 'Escaneos por Fuente'}</h4>
        <div className="space-y-2">
          {Object.entries(analytics.source_breakdown || {}).map(([source, count]) => {
            const percentage = (count / analytics.total_scans * 100).toFixed(1);
            return (
              <div key={source} className="flex items-center gap-3">
                <div className={`w-8 h-8 ${sourceColors[source] || sourceColors.unknown} rounded-lg flex items-center justify-center text-white`}>
                  {sourceIcons[source] || sourceIcons.unknown}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize">{source}</span>
                    <span className="text-muted-foreground">{count} ({percentage}%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${sourceColors[source] || sourceColors.unknown} rounded-full transition-all`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Kupons */}
      {analytics.top_kupons && analytics.top_kupons.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-3">{t('qr_analytics.top_kupons') || 'Kupones Más Escaneados'}</h4>
          <div className="space-y-2">
            {analytics.top_kupons.slice(0, 5).map((kupon, idx) => (
              <div 
                key={kupon.kupon_id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-sm font-medium truncate max-w-[200px]">
                    {kupon.kupon_id.slice(0, 8)}...
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">{kupon.scans} escaneos</span>
                  <span className={`font-medium ${kupon.rate > 10 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                    {kupon.rate}% conv.
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Trend */}
      {analytics.scans_trend && analytics.scans_trend.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium mb-3">{t('qr_analytics.trend') || 'Tendencia Semanal'}</h4>
          <div className="flex items-end gap-1 h-24">
            {analytics.scans_trend.map((week, idx) => {
              const maxScans = Math.max(...analytics.scans_trend.map(w => w.scans));
              const height = maxScans > 0 ? (week.scans / maxScans * 100) : 0;
              return (
                <div
                  key={week.week}
                  className="flex-1 bg-purple-200 dark:bg-purple-800 rounded-t hover:bg-purple-300 dark:hover:bg-purple-700 transition-colors cursor-pointer group relative"
                  style={{ height: `${Math.max(height, 5)}%` }}
                  title={`${week.week}: ${week.scans} escaneos`}
                >
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {week.scans}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-2">
            <span>{analytics.scans_trend[0]?.week}</span>
            <span>{analytics.scans_trend[analytics.scans_trend.length - 1]?.week}</span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default QRAnalyticsDashboard;
